/**
 * The boxes inside the sticky page header that a page may portal into.
 *
 * ## Why (shrinkhead, 2 Oct 2026)
 *
 * A page's buttons and its tab strip used to render BELOW the header, in the
 * page, so both scrolled away on the first flick and the header that stayed
 * behind could not reach them. Founder, 2 Oct 2026, on Reports: *"while
 * scrolling down the header should become small in height and all these
 * options should go up there so that maximum realestate is kept for the main
 * view"*. So the header carries three hosts and the page hands its pieces to
 * them: the slot (`HeaderSlot`: year, stage), the actions (`PageHeader`'s
 * `actions`) and the tabs (`PageTabs`).
 *
 * A fourth since 11 Oct 2026 (headstats): the figures (`HeaderFigures`: how
 * much). The page's summary numbers moved out of a row of cards under the
 * header and into the place the hover drawing used to hold, so the slot says
 * which year, the figures how much, the tabs which view and the actions do
 * something.
 *
 * Each id names one box, rendered ONCE by the header that draws the bar, and
 * the same element in its tall and compact shapes, so a collapse never
 * remounts what a page put there.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

/** Where a page's `<PageHeader actions>` land. */
export const HEADER_ACTIONS_ID = 'karr-page-header-actions';
/** Where a page's `<PageTabs>` land. */
export const HEADER_TABS_ID = 'karr-page-header-tabs';
/** Where a page's `<HeaderFigures>` land (headstats). */
export const HEADER_FIGURES_ID = 'karr-page-header-figures';
/** The bar itself, which carries `data-compact` while it is one line. */
export const HEADER_ROOT_ID = 'karr-page-header';

/**
 * The header box carrying `id`, once it exists, followed through re-renders.
 *
 * It is not there on the first render of a route's children in every case, and
 * a header that re-renders can REPLACE it — a different element, same id. So
 * this watches rather than looking once: a MutationObserver on the body, whose
 * callback is one `getElementById`.
 *
 * Shared by every portal into the header: `HeaderSlot`, the page's `actions`
 * (`PageHeader`) and its tabs (`PageTabs`).
 */
export function useHeaderHost(id: string, enabled = true): HTMLElement | null {
    const [host, setHost] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!enabled) return;
        const find = () => {
            const el = document.getElementById(id);
            setHost(prev => (prev === el ? prev : el));
        };
        find();
        const mo = new MutationObserver(find);
        mo.observe(document.body, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, [id, enabled]);

    return enabled ? host : null;
}

function subscribeCompact(notify: () => void) {
    const mo = new MutationObserver(notify);
    mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-compact'] });
    return () => mo.disconnect();
}
const readCompact = () => document.getElementById(HEADER_ROOT_ID)?.hasAttribute('data-compact') ?? false;
const serverCompact = () => false;

/**
 * Whether the page header is in its one-line, scrolled shape (reportsplus).
 * For something a page shows in the bar ONLY when the full controls under it
 * have scrolled away, such as the Reports period.
 */
export function useHeaderCompact(): boolean {
    return useSyncExternalStore(subscribeCompact, readCompact, serverCompact);
}

function subscribeFigures(notify: () => void) {
    const mo = new MutationObserver(notify);
    mo.observe(document.body, { subtree: true, childList: true });
    return () => mo.disconnect();
}
const readFigures = () => (document.getElementById(HEADER_FIGURES_ID)?.childElementCount ?? 0) > 0;
const serverFigures = () => false;

/**
 * Whether a page has put figures in the header (headstats). The header then
 * leaves out the registry subtitle: "Manage bills and vendor payments" says
 * less than "Payable ₹0.00" ever did, and the line it took is the list's.
 * A page whose figures all came to nothing renders none, and the subtitle
 * comes back.
 */
export function useHeaderHasFigures(): boolean {
    return useSyncExternalStore(subscribeFigures, readFigures, serverFigures);
}
